<template>
  <div class="flex gap-[14px] items-center !flex-nowrap">
    <template :key="index" v-for="index in numInputs">
      <otp-cell
          :active="isCurrentInputActive(index - 1)"
          :input-type="inputType"
          :model-value="data[index - 1]"
          :ref="
        (el) => {
          cellRefs.push(el);
        }
      "
          :error="error"
          @next="onNext"
          @prev="onPrev"
          @focus="onFocus(index - 1)"
          @update:model-value="onUpdate(index, $event)"
          @paste="onPaste"
      />
      <div v-if="isExistsSeparator(index)">-</div>
    </template>
  </div>
</template>

<script setup>
import { nextTick, onMounted, ref, watch } from 'vue'
import OtpCell from './OtpCell.vue'

const SEPARATOR_TYPES = {
  HALF: 'half',
  EVEN: 'even',
  ODD: 'odd',
  ALL: 'all',
}

const props = defineProps({
  numInputs: {
    type: Number,
    default: 6,
  },
  separator: {
    type: String,
    default: '',
  },
  separateBy: {
    type: [String, Number],
    default: 'half',
  },
  error: {
    type: Boolean,
    default: false,
  },
  inputType: {
    type: String,
    validator: (type) => ['number', 'tel', 'password'].includes(type),
    default: 'number',
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
})
const emit = defineEmits(['complete', 'change', 'update:modelValue'])

const cellRefs = ref([])
const activeInput = ref(0)

const data = ref([])
const onUpdate = (order, value) => {
  data.value[order - 1 || 0] = value || null

  emit('change')
  emit('update:modelValue', data.value.join(''))

  const isAllFieldsAreFilled =
      data.value.every((input) => input) && data.value.length === props.numInputs
  if (isAllFieldsAreFilled) {
    emit('complete', data.value.join(''))
  }
}

const onNext = () => {
  focusInput(activeInput.value + 1)
}

const onPrev = () => {
  focusInput(activeInput.value - 1)
}

const onFocus = (index) => {
  focusInput(index)
}

const onPaste = (value) => {
  emit('update:modelValue', value)
}

const focusInput = (input) => {
  activeInput.value = Math.max(Math.min(props.numInputs - 1, input), 0)
}

const isCurrentInputActive = (index) => {
  return activeInput.value === index
}

const isExistsSeparator = (index) => {
  if (!props.separator) return false

  switch (props.separateBy) {
    case SEPARATOR_TYPES.HALF:
      return index === Math.floor(props.numInputs / 2)
    case SEPARATOR_TYPES.EVEN:
      return index % 2 === 0
    case SEPARATOR_TYPES.ODD:
      return index % 2 !== 0
    default:
      return Number.isInteger(props.separateBy) && index % props.separateBy === 0
  }
}

watch(() => props.modelValue,
    (value) => {
      data.value = Array.from(value)
    },
    { immediate: true }
)

onMounted(() => {
  nextTick(() => {
    cellRefs.value[0].$refs.input.focus()
  })
})
</script>
